import { useState, useEffect } from 'react';
import { useGooglePlaces } from '../../hooks/useGooglePlaces';
import PlaceCard from './PlaceCard';
import LoadingState from '../shared/LoadingState';
import Button from '../shared/Button';
import useTripStore from '../../context/TripStore';

const PlaceSearch = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState(null);
  const [searchMarkers, setSearchMarkers] = useState([]);
  const { search, isLoaded } = useGooglePlaces();
  const { trip, places } = useTripStore();

  // Show search results on map
  useEffect(() => {
    // Clear previous search markers
    searchMarkers.forEach(marker => marker.setMap(null));
    
    if (results.length === 0 || !window.google?.maps || !window.backgroundMapInstance) {
      setSearchMarkers([]);
      return;
    }

    // Create blue markers for search results
    const markers = results.map(result => {
      if (!result.coordinates) return null;
      
      return new window.google.maps.Marker({
        position: result.coordinates,
        map: window.backgroundMapInstance,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 8,
          fillColor: '#3B82F6', // Blue for search results
          fillOpacity: 0.8,
          strokeColor: '#FFFFFF',
          strokeWeight: 2,
        },
        title: result.name,
        zIndex: 500, // Below itinerary markers but visible
      });
    }).filter(Boolean);

    setSearchMarkers(markers);

    // Fit bounds to show search results if we have them
    if (markers.length > 0 && window.backgroundMapInstance) {
      const bounds = new window.google.maps.LatLngBounds();
      markers.forEach(marker => bounds.extend(marker.getPosition()));
      window.backgroundMapInstance.fitBounds(bounds);
    }

    return () => {
      markers.forEach(marker => marker.setMap(null));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results]);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!query.trim() || !isLoaded) return;

    setIsSearching(true);
    setError(null);

    try {
      // Create bounds from city if available
      let cityBounds = null;
      if (trip.city?.coordinates) {
        const { lat, lng } = trip.city.coordinates;
        cityBounds = new window.google.maps.LatLngBounds(
          new window.google.maps.LatLng(lat - 0.1, lng - 0.1),
          new window.google.maps.LatLng(lat + 0.1, lng + 0.1)
        );
      }

      const searchResults = await search(query, cityBounds);
      // Keep all results, but we'll show which ones are already in the list
      setResults(searchResults);
    } catch (err) {
      setError(err.message);
      setResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <div className="mb-6">
      <form onSubmit={handleSearch} className="mb-4">
        <div className="flex gap-2">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search for places, restaurants, attractions..."
            className="flex-1 px-3 py-2 text-sm font-light border border-gray-300 focus:outline-none focus:border-black"
            disabled={!isLoaded || isSearching}
          />
          <Button type="submit" disabled={!isLoaded || isSearching || !query.trim()}>
            {isSearching ? 'Searching...' : 'Search'}
          </Button>
        </div>
      </form>

      {error && (
        <div className="border border-gray-300 text-xs font-light text-gray-700 px-3 py-2 mb-4">
          {error}
        </div>
      )}

      {isSearching ? (
        <LoadingState message="Searching places..." />
      ) : results.length > 0 ? (
        <div className="space-y-3">
          <h3 className="text-xs font-light uppercase tracking-wide text-gray-600 mb-3">Search Results</h3>
          {results.map((place) => {
            // Check if this place is already in the list
            const isInList = places.some(p => p.placeId === place.placeId);
            const listPlace = places.find(p => p.placeId === place.placeId);
            return (
              <PlaceCard 
                key={place.placeId} 
                place={isInList ? { ...place, id: listPlace.id, priority: listPlace.priority } : place}
                isInList={isInList}
              />
            );
          })}
        </div>
      ) : query && !isSearching ? (
        <p className="text-xs font-light text-gray-400 text-center py-4">No results found. Try a different search.</p>
      ) : null}
    </div>
  );
};

export default PlaceSearch;

